import { useCallback, useMemo } from "react";

import { BASE_UNITS, UnitCategory, UnitConversionResult } from "types/units";

import { useEvaUnitCore } from "./useEvaUnitCore";
import useUserUnit from "./useUserUnit";

export function useUnitConversion(category: UnitCategory): UnitConversionResult {
  const unitType = useUserUnit();
  const { isReady, getTargetUnit, convertByUnit, convertUnitToUnit } = useEvaUnitCore();

  const baseUnit = BASE_UNITS[category];

  // Get the target display unit based on user preference
  const displayUnit = useMemo(() => {
    if (!isReady) return baseUnit;
    return getTargetUnit(baseUnit, unitType);
  }, [isReady, baseUnit, unitType, getTargetUnit]);

  // Convert from base unit to display unit (for showing values)
  // By default, rounds to whole numbers (0). Pass precision to control decimal places.
  const convert = useCallback(
    (valueInBaseUnit: number, precision: number = 0): number => {
      if (!isReady || valueInBaseUnit === null || valueInBaseUnit === undefined) {
        return valueInBaseUnit;
      }
      const result = convertByUnit(baseUnit, unitType, valueInBaseUnit, precision);
      // WASM identity conversion (same unit, e.g. m→m in Hybrid) skips rounding.
      // Apply explicit rounding so precision is always honoured.
      if (precision >= 0 && isFinite(result)) {
        const factor = Math.pow(10, precision);
        return Math.round(result * factor) / factor;
      }
      return result;
    },
    [isReady, baseUnit, unitType, convertByUnit]
  );

  // Convert from display unit back to base unit (for storing values)
  const convertBack = useCallback(
    (valueInDisplayUnit: number, precision: number = 2): number => {
      if (!isReady || valueInDisplayUnit === null || valueInDisplayUnit === undefined) {
        return valueInDisplayUnit;
      }
      return convertUnitToUnit(displayUnit, baseUnit, valueInDisplayUnit, precision);
    },
    [isReady, displayUnit, baseUnit, convertUnitToUnit]
  );

  const getDisplayUnit = useCallback(() => displayUnit, [displayUnit]);

  const getBaseUnit = useCallback(() => baseUnit, [baseUnit]);

  return {
    isReady,
    convert,
    convertBack,
    convertUnitToUnit,
    getDisplayUnit,
    getBaseUnit,
    unitType
  };
}

/**
 * commonly used for XDA radius, well spacing, depths, etc.
 */
export function useLengthConversion(): UnitConversionResult {
  return useUnitConversion(UnitCategory.Length);
}
export function usePressureConversion(): UnitConversionResult {
  return useUnitConversion(UnitCategory.Pressure);
}

export function useTemperatureConversion(): UnitConversionResult {
  return useUnitConversion(UnitCategory.Temperature);
}

export function useAreaConversion(): UnitConversionResult {
  return useUnitConversion(UnitCategory.Area);
}

export function useVolumeConversion(): UnitConversionResult {
  return useUnitConversion(UnitCategory.Volume);
}

export default useUnitConversion;
